import React, { useContext, useState } from "react";
import {
  Grid,
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Divider,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { toast } from "react-toastify";
import ColorModeContext from "../../layout/themeContext";

/**
 * Selector de tema de la empresa (doc 0390, FT-5).
 *
 * ALCANCE: el tema elegido aquí afecta a TODOS los usuarios de la empresa, no
 * solo a quien lo cambia (decisión D9). Por eso se avisa explícitamente antes de
 * elegir: un ajuste con alcance colectivo presentado como preferencia personal
 * es una trampa para quien lo pulsa.
 *
 * Lo que cada persona sí elige por su cuenta —claro u oscuro— vive en el menú de
 * usuario de la barra superior, no aquí.
 */
const useStyles = makeStyles(theme => ({
  contenedor: {
    padding: theme.spacing(2)
  },
  aviso: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  opcion: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    width: "100%"
  },
  opcionActiva: {
    borderColor: theme.palette.primary.main
  },
  descripcion: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  cabeceraOpcion: {
    display: "flex",
    alignItems: "center",
    gap: 8
  },
  vacio: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2)
  },
  separador: {
    margin: theme.spacing(3, 0, 2)
  },
  colores: {
    display: "flex",
    gap: theme.spacing(3),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  campoColor: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer"
  },
  etiquetaModo: {
    fontSize: 13,
    color: theme.palette.text.secondary
  },
  entradaColor: {
    width: 38,
    height: 28,
    padding: 0,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    background: "none",
    cursor: "pointer"
  },
  valorColor: {
    fontSize: 12,
    color: theme.palette.text.secondary
  }
}));

const ThemeSelector = () => {
  const classes = useStyles();
  const { colorMode } = useContext(ColorModeContext);
  const [guardando, setGuardando] = useState(false);

  const temas = (colorMode && colorMode.themes) || [];
  const activo = colorMode && colorMode.themeSlug;

  const cambiar = async slug => {
    if (!slug || slug === activo || guardando) return;

    setGuardando(true);
    try {
      await colorMode.setTheme(slug);
      toast.success("Tema aplicado para toda la empresa");
    } catch (e) {
      // El servicio propaga el error a propósito: fallar en silencio le haría
      // creer al administrador que el cambio quedó guardado.
      const mensaje =
        (e && e.response && e.response.data && e.response.data.error) ||
        "No se pudo cambiar el tema";
      toast.error(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  const acentoActual = (modo) => {
    const t = ((colorMode && colorMode.tokens) || []).find(
      x => x.tokenKey === "accent" && x.mode === modo
    );
    return (t && t.tokenValue) || "#1e3a8a";
  };

  const cambiarAcento = async (modo, color) => {
    if (guardando) return;

    setGuardando(true);
    try {
      const r = await colorMode.setAccent(modo, color);
      toast.success(
        r && r.creado
          ? "Tu empresa pasa a tener un tema propio con este color"
          : "Color de acento actualizado"
      );
    } catch (e) {
      const mensaje =
        (e && e.response && e.response.data && e.response.data.error) ||
        "No se pudo cambiar el color";
      toast.error(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  if (temas.length === 0) {
    return (
      <div className={classes.vacio}>
        <Typography variant="body2">
          No hay temas disponibles todavía.
        </Typography>
      </div>
    );
  }

  return (
    <Grid container spacing={2} className={classes.contenedor}>
      <Grid item xs={12}>
        <Typography variant="body2" className={classes.aviso}>
          El tema se aplica a <strong>todos los usuarios de la empresa</strong>.
          Cada persona sigue eligiendo por su cuenta si lo ve en claro u oscuro.
        </Typography>
      </Grid>

      <Grid item xs={12} md={8}>
        <RadioGroup value={activo || ""} onChange={e => cambiar(e.target.value)}>
          {temas.map(tema => (
            <Paper
              key={tema.slug}
              elevation={0}
              className={`${classes.opcion} ${
                tema.slug === activo ? classes.opcionActiva : ""
              }`}
            >
              <FormControlLabel
                value={tema.slug}
                disabled={guardando}
                control={<Radio color="primary" />}
                label={
                  <div>
                    <div className={classes.cabeceraOpcion}>
                      <Typography variant="subtitle2">{tema.name}</Typography>
                      {tema.isSystem && (
                        <Chip size="small" label="Del sistema" variant="outlined" />
                      )}
                      {tema.slug === activo && (
                        <Chip size="small" label="En uso" color="primary" />
                      )}
                    </div>
                    {tema.description && (
                      <Typography className={classes.descripcion}>
                        {tema.description}
                      </Typography>
                    )}
                  </div>
                }
              />
            </Paper>
          ))}
        </RadioGroup>
      </Grid>

      <Grid item xs={12} md={8}>
        <Divider className={classes.separador} />
        <Typography variant="subtitle2">Color de acento</Typography>
        <Typography variant="body2" className={classes.aviso}>
          Se usa en el elemento activo del menú, los enlaces y la acción
          principal. Al cambiarlo, tu empresa pasa a tener un tema propio: los
          temas del sistema no se modifican, para que siempre puedas volver a
          ellos.
        </Typography>

        <div className={classes.colores}>
          {["light", "dark"].map(modo => (
            <label key={modo} className={classes.campoColor}>
              <span className={classes.etiquetaModo}>
                {modo === "light" ? "Modo claro" : "Modo oscuro"}
              </span>
              <input
                type="color"
                className={classes.entradaColor}
                value={acentoActual(modo)}
                disabled={guardando}
                onChange={e => cambiarAcento(modo, e.target.value)}
              />
              <code className={classes.valorColor}>{acentoActual(modo)}</code>
            </label>
          ))}
        </div>
      </Grid>

      {guardando && (
        <Grid item xs={12}>
          <CircularProgress size={20} />
        </Grid>
      )}
    </Grid>
  );
};

export default ThemeSelector;
